import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "../i18n";
import { useNavigate } from "react-router-dom";

import "@fontsource/nunito/400.css";
import "@fontsource/nunito/600.css";
import "@fontsource/nunito/700.css";
import "@fontsource/nunito/900.css";

import "./brand/tokens.css";
import "./RestaurantCard.css";
import "./SwipeDeck.css";
import "./DietaryFilterBar.css";
import "./DiscoveryScreen.css";
import "./MapView.css";

import dataService from "../../services/dataService";
import useDiscoveryStore from "./useDiscoveryStore";
import DietaryFilterBar from "./DietaryFilterBar";
import SwipeDeck from "./SwipeDeck";
import MapView from "./MapView";
import LogoMarkSVG from "./brand/LogoMarkSVG";
import MascotFloat from "./MascotFloat";
import { Logo } from "../brand/Primitives";
import { FAKE_RESTAURANTS } from "../demo/fakeData";
import { useAuthModal } from "../auth/AuthModalContext";
import { canonicalizeTag } from "./taxonomy";
import { mockFriendsFor } from "./mockFriends";

/**
 * The complete discovery module — brings together filter bar, swipe
 * deck, and store. Meant to be dropped anywhere the app needs a
 * "browse restaurants" experience.
 *
 * Props:
 *   userId         : number | null   (null = anonymous; state stays local-only)
 *   withBackdrop   : boolean         (true = bubble-blue sky background)
 *   title, subtitle : optional custom header text
 */
// Set equality + friendly tag list formatter (used by the auto-filter
// banner). Stays local so we don't pollute the public feature surface.
function setsMatch(a, b) {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}
function formatTagList(tags) {
  if (!tags.length) return '';
  if (tags.length === 1) return tags[0];
  if (tags.length === 2) return `${tags[0]} & ${tags[1]}`;
  return `${tags.slice(0, -1).join(', ')} & ${tags[tags.length - 1]}`;
}

export default function DiscoveryScreen({
  userId = null,
  withBackdrop = false,
  embedded = false,
  title = "Restaurants your body will thank you for",
  subtitle = "Swipe right to save, left to skip — we'll learn what works for you.",
  initialTags = [],
}) {
  const navigate = useNavigate();
  const store = useDiscoveryStore(userId);
  const { openAuth } = useAuthModal();
  const { t } = useI18n();

  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [search, setSearch] = useState("");
  const [cuisine, setCuisine] = useState("All");
  const [type, setType] = useState("All");
  const [sort, setSort] = useState("Best Match");
  const [activeTags, setActiveTags] = useState(() => new Set(initialTags));
  const [autoAppliedTags, setAutoAppliedTags] = useState(() => new Set(initialTags));
  const [view, setView] = useState("list");

  // When initialTags change (async preferences load), seed the active set once.
  useEffect(() => {
    if (initialTags && initialTags.length > 0) {
      setActiveTags((prev) => {
        if (prev.size > 0) return prev; // don't overwrite user-selected tags
        return new Set(initialTags);
      });
      setAutoAppliedTags(new Set(initialTags));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTags.join(',')]);

  // Load once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await dataService.listRestaurants();
        const raw = Array.isArray(data) ? data : data.restaurants || [];
        // Backend uses `latitude`/`longitude`; MapboxMap expects `lat`/`lng`.
        // Normalize once here so list view, swipe deck, and map share the same shape.
        const rows = raw.map((r) => ({
          ...r,
          lat: r.lat ?? (Number.isFinite(r.latitude) ? r.latitude : undefined),
          lng: r.lng ?? (Number.isFinite(r.longitude) ? r.longitude : undefined),
        }));
        if (!cancelled) setRestaurants(rows);
      } catch (e) {
        if (!cancelled) {
          // Graceful fallback: when the backend isn't reachable (dev / preview),
          // seed the deck from the bundled demo restaurants so the user can
          // still see the layout, the filter bar, and the swipe deck working.
          const demo = FAKE_RESTAURANTS.map((r, i) => ({
            id: 1000 + i,
            name: r.name,
            cuisine_type: r.cuisine,
            category: r.cuisine,
            price_range: r.price,
            display_rating: r.rating,
            display_review_count: 120 + i * 37,
            description: r.blurb,
            is_open: r.open,
            photo_url: r.photo,
            photo_count: 1,
            address: "New York, NY",
            city: "New York",
            state: "NY",
            lat: r.lat,
            lng: r.lng,
            dietary_display_tags: [...(r.safe || []), ...(r.warn || [])].map((t) => t.label),
          }));
          setRestaurants(demo);
          setError(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const cuisines = useMemo(() => {
    const set = new Set();
    restaurants.forEach((r) => { if (r.cuisine_type) set.add(r.cuisine_type); });
    return Array.from(set).sort();
  }, [restaurants]);

  const toggleTag = (tag) => {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag); else next.add(tag);
      return next;
    });
  };
  const clearTags = () => setActiveTags(new Set());

  // Derive filtered restaurants
  const filtered = useMemo(() => {
    let out = restaurants.slice();
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.cuisine_type || "").toLowerCase().includes(q)
      );
    }
    if (cuisine && cuisine !== "All") {
      out = out.filter((r) => r.cuisine_type === cuisine);
    }
    // Type pills map to our data's semantics
    if (type === "Liked") {
      out = out.filter((r) => store.isSaved(r.id));
    } else if (type === "Disliked") {
      out = out.filter((r) => store.isSkipped(r.id));
    } else if (type === "Loved by Friends") {
      out = out.filter((r) => mockFriendsFor(r.id).names.length > 0);
    } else if (type === "Bars & Drinks") {
      out = out.filter((r) => /bar|brew|pub|tavern/i.test(r.cuisine_type || ""));
    } else if (type === "Cafes & Treats") {
      out = out.filter((r) => /cafe|bakery|dessert|ice cream|coffee/i.test(r.cuisine_type || ""));
    } else if (type === "Restaurants") {
      out = out.filter((r) => !/bar|brew|pub|tavern|cafe|bakery|dessert|ice cream|coffee/i.test(r.cuisine_type || ""));
    }
    // Dietary: AND-match — restaurant must have all selected tags
    if (activeTags.size > 0) {
      out = out.filter((r) => {
        const tagSet = new Set();
        const cards = r.card_tags;
        if (Array.isArray(cards)) cards.forEach((t) => tagSet.add(canonicalizeTag(t)));
        else if (typeof cards === "string") {
          try { JSON.parse(cards).forEach((t) => tagSet.add(canonicalizeTag(t))); } catch {}
        }
        const dd = r.dietary_display_tags;
        if (Array.isArray(dd)) dd.forEach((t) => tagSet.add(canonicalizeTag(t)));
        const dt = r.dietary_tags;
        if (typeof dt === "string") {
          try { JSON.parse(dt).forEach((t) => tagSet.add(canonicalizeTag(t))); } catch {}
        }
        for (const t of activeTags) if (!tagSet.has(t)) return false;
        return true;
      });
    }
    if (sort === "Rating") {
      out.sort((a, b) => (b.display_rating || 0) - (a.display_rating || 0));
    } else if (sort === "Most Reviewed") {
      out.sort((a, b) => (b.display_review_count || 0) - (a.display_review_count || 0));
    } else if (sort === "Distance") {
      // Without geolocation, fall back to alphabetical
      out.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }
    return out;
  }, [restaurants, search, cuisine, type, sort, activeTags, store]);

  const onChat = (restaurant) => {
    // Chat requires an account. Anonymous visitors get prompted to sign in;
    // once they're authed, the caller navigates into the chat screen.
    if (!userId) {
      openAuth("signin");
      return;
    }
    navigate("/unified/chat", { state: { restaurantId: restaurant.id, restaurantName: restaurant.name } });
  };

  return (
    <section
      className="cwm-discovery"
      data-with-backdrop={withBackdrop ? "true" : "false"}
      data-embedded={embedded ? "true" : "false"}
    >
      <div className="cwm-discovery-inner">
        {/* Brand bar — hidden when embedded (dashboard panel already titles
            the section). Shown only for the standalone /discover route. */}
        {!embedded && (
          <header className="cwm-brand">
            <Logo variant="stacked-white" height={96} />
            <div className="cwm-brand-tagline">{t('discover.brand_tagline')}</div>
          </header>
        )}

        <DietaryFilterBar
          search={search} onSearchChange={setSearch}
          cuisine={cuisine} cuisines={cuisines} onCuisineChange={setCuisine}
          type={type} onTypeChange={setType}
          sort={sort} onSortChange={setSort}
          activeTags={activeTags}
          onToggleTag={toggleTag}
          onClearTags={() => { clearTags(); setAutoAppliedTags(new Set()); }}
          resultCount={filtered.length}
          totalCount={restaurants.length}
          city="New York City, NY"
          view={view}
          onViewChange={setView}
        />

        {/* Auto-applied dietary banner — only when we seeded tags from the
            user's onboarding profile AND they haven't diverged from that
            auto set. Gives a one-click "show everything" escape hatch. */}
        {autoAppliedTags.size > 0 && setsMatch(activeTags, autoAppliedTags) && (
          <div className="cwm-auto-filter-banner" role="status">
            <span className="cwm-auto-filter-banner__icon" aria-hidden>✦</span>
            <div className="cwm-auto-filter-banner__text">
              <strong>{t('discover.auto_banner_title')}</strong>
              <span>{t('discover.auto_banner_body', { tags: formatTagList(Array.from(autoAppliedTags)) })}</span>
            </div>
            <button
              type="button"
              className="cwm-auto-filter-banner__clear"
              onClick={() => { clearTags(); setAutoAppliedTags(new Set()); }}
            >
              {t('discover.clear_auto')}
            </button>
          </div>
        )}

        {loading && (
          <div
            className="cwm-discovery-state"
            aria-live="polite"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16, padding: '16px' }}
          >
            <style>{`
              @keyframes cwmShimmer {
                0%   { background-position: 200% 0; }
                100% { background-position: -200% 0; }
              }
              .cwm-disc-sk {
                background: linear-gradient(90deg, #fff 0%, var(--cwm-blue-pale, #EAF4FF) 50%, #fff 100%);
                background-size: 200% 100%;
                animation: cwmShimmer 1.4s ease-in-out infinite;
                border-radius: 20px;
              }
              @media (prefers-reduced-motion: reduce) {
                .cwm-disc-sk { animation: none; background: #fff; }
              }
            `}</style>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="cwm-disc-sk" style={{ height: 320 }} aria-hidden />
            ))}
            <span style={{ position: 'absolute', left: -9999 }}>Loading restaurants…</span>
          </div>
        )}
        {error && !loading && (
          <div className="cwm-discovery-state cwm-discovery-error">{error}</div>
        )}
        {!loading && !error && view !== "map" && (
          <SwipeDeck
            restaurants={filtered}
            store={store}
            onChat={onChat}
            onShowDetails={(r) => navigate(`/unified/restaurant/${r.id}`)}
            activeTagSet={activeTags}
            onTagClick={toggleTag}
            /* Type-pill "Disliked" lives in the filter bar and filters
               the upstream list down to skipped-only rows. SwipeDeck's
               default hide-skipped behavior would then wipe them too,
               producing an empty deck. When the user is explicitly
               browsing their dislikes we stop hiding. */
            hideSkipped={type !== "Disliked"}
          />
        )}
        {!loading && !error && view === "map" && (
          <MapView restaurants={filtered} onChat={onChat} />
        )}
      </div>
      {/* FIX #12 — floating mascot. Only on the public/full-backdrop view
          to avoid covering the dashboard's existing account section. */}
      {withBackdrop && <MascotFloat />}
    </section>
  );
}
